import { format } from "date-fns";
import { Link } from "react-router-dom";
import { useDrillDetail } from "../../MultiSectionBatchesUpdateDrillPage/useDrillDetail";
import { MultiSectionBatch } from "../useMultiSectionBatchesDetail";

type Props = {
  batch: MultiSectionBatch;
  fromRegistration: boolean;
};

const DrillInfoCard = ({ materialCode }: { materialCode: string }) => {
  const { drill } = useDrillDetail(materialCode);

  if (!drill) {
    return null;
  }

  return (
    <div className="flex items-center gap-[24px] rounded-md border border-solid border-gray-400 bg-white p-[16px]">
      {drill.imageUrl ? (
        <img
          src={drill.imageUrl}
          alt={drill.name}
          className="h-[78px] w-[59px] shrink-0 rounded-sm object-cover"
        />
      ) : (
        <div className="h-[78px] w-[59px] shrink-0 rounded-sm bg-gray-300" />
      )}
      <div className="flex flex-1 flex-col gap-[10px] min-w-0">
        <p className="text-[14px] font-bold break-words">{drill.name}</p>
        <p className="text-[14px]">セクション：{drill.sections.length}</p>
      </div>
    </div>
  );
};

const Success = ({
  endedAt,
  fromRegistration,
}: {
  endedAt: string | null;
  fromRegistration: boolean;
}) => (
  <div className="flex flex-col gap-8 rounded-lg bg-gray-100 px-10 py-[22px]">
    <div className="flex flex-col gap-8">
      <p className="text-blue-400">
        デジタル教材を更新しました
        {!fromRegistration && endedAt && (
          <span>（{format(new Date(endedAt), "yyyy/MM/dd HH:mm")}）</span>
        )}
      </p>
      <p>更新したデジタル教材は登録一覧から確認できます。</p>
      <span>
        <Link
          to="/multi_section_batches/upload_drill/list"
          className="buiButtonBase buiButtonVariantOutline buiButtonSizeSm buiButtonRound"
        >
          登録一覧を見る
        </Link>
      </span>
    </div>
  </div>
);

const Failure = ({
  feedback,
  fromRegistration,
  materialCode,
}: {
  feedback: string | null;
  fromRegistration: boolean;
  materialCode: string;
}) => (
  <div className="flex flex-col gap-8 rounded-lg bg-gray-100 px-10 py-[22px]">
    <div className="flex flex-col gap-4">
      <p className="font-bold text-red-400">デジタル教材の更新に失敗しました</p>
      {feedback &&
        feedback
          .split("\n")
          .filter((line) => line.trim() !== "")
          .map((line, index) => (
            <p key={index} className="text-red-400">
              {line}
            </p>
          ))}
      <p className="text-red-400">データを確認して再度更新を行ってください。</p>
    </div>
    {fromRegistration && materialCode && (
      <span>
        <Link
          to={`/multi_section_batches/update_drill/${materialCode}`}
          className="buiButtonBase buiButtonVariantOutline buiButtonSizeSm buiButtonRound"
        >
          更新画面に戻る
        </Link>
      </span>
    )}
  </div>
);

const Pending = () => (
  <div className="flex flex-col gap-8 rounded-lg bg-gray-100 px-10 py-[22px]">
    <div className="flex flex-col gap-4">
      <p className="font-bold">デジタル教材を更新中です。</p>
      <p>処理が完了すると、校舎横断操作の完了メールをお送りします。</p>
      <p>メール内のリンクから、更新したデジタル教材をご確認いただけます。</p>
      <p>※件数が多い場合、完了までに時間がかかることがあります。</p>
    </div>
    <span>
      <button
        type="button"
        className="buiButtonBase buiButtonVariantOutline buiButtonSizeSm buiButtonRound"
        onClick={() => window.location.reload()}
      >
        再読み込み
      </button>
    </span>
  </div>
);

const renderStatus = (
  batch: MultiSectionBatch,
  fromRegistration: boolean,
  materialCode: string,
) => {
  switch (batch.status) {
    case "standby":
    case "processing":
      return <Pending />;
    case "success":
      return (
        <Success endedAt={batch.endedAt} fromRegistration={fromRegistration} />
      );
    case "failure":
      return (
        <Failure
          feedback={batch.feedback ?? null}
          fromRegistration={fromRegistration}
          materialCode={materialCode}
        />
      );
  }
};

export const UpdateDrillDetail = ({ batch, fromRegistration }: Props) => {
  const materialCode = batch.payload?.steakMaterialCode ?? "";
  const isPending = batch.status === "standby" || batch.status === "processing";

  return (
    <div className="flex flex-col gap-4">
      {!isPending && materialCode && (
        <DrillInfoCard materialCode={materialCode} />
      )}
      {renderStatus(batch, fromRegistration, materialCode)}
    </div>
  );
};
